// Accountant dashboard panel: the Needs-review queue, first thing on the
// page. Every row is clickable through to the deal, with inline Approve /
// Fix actions (same writes as the Commission Approvals page — shared patch
// builders). Renders only for the Accountant role (hero.jsx gates it).
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Calculator, CheckCircle2, ChevronRight, Flag } from "lucide-react";
import { getAllusers, updateItem } from "../../utils/firebasefunctions";
import { formatCurrency, notifyCommissionFlagged } from "../../utils/helper";
import {
  accountingTab,
  buildAccountingApprovePatch,
  buildAccountingFixPatch,
} from "../../utils/accountingReview";
import { isDemoUser } from "../../utils/roles";
import FixDealDialog from "./FixDealDialog";

const splitLabel = (deal) => {
  if (deal?.fixedagentcommission) {
    return `flat ${formatCurrency(deal.agentCommission)}`;
  }
  const pct = Number(deal?.agentCommission);
  return pct ? `agent ${pct}%` : "";
};

const MAX_ROWS = 6;

export default function AccountingApprovalsPanel({
  deals,
  users,
  currentUser,
  loaded = true,
}) {
  const navigate = useNavigate();
  const canAct = currentUser?.role === "Accountant" && !isDemoUser(currentUser);

  const [busyId, setBusyId] = useState("");
  const [fixTarget, setFixTarget] = useState(null);
  // Rows acted on this session disappear immediately without refetching.
  const [doneIds, setDoneIds] = useState(() => new Set());

  const queue = useMemo(
    () =>
      (deals || [])
        .filter((d) => accountingTab(d) === "needs-review")
        .filter((d) => !doneIds.has(String(d.createdat)))
        .sort((a, b) => Number(b.createdat || 0) - Number(a.createdat || 0)),
    [deals, doneIds],
  );

  const markDone = (id) =>
    setDoneIds((prev) => new Set(prev).add(String(id)));

  const approveDeal = async (deal) => {
    if (busyId) return;
    const id = String(deal.createdat);
    setBusyId(id);
    const res = await updateItem(
      "deals",
      id,
      buildAccountingApprovePatch(currentUser?.id, Date.now()),
    );
    setBusyId("");
    if (res === "success") markDone(id);
    else toast.error("Couldn't save the approval. Try again.");
  };

  const submitFix = async (note) => {
    if (!fixTarget || !note || busyId) return;
    const id = String(fixTarget.createdat);
    setBusyId(id);
    const res = await updateItem(
      "deals",
      id,
      buildAccountingFixPatch(currentUser?.id, note, Date.now()),
    );
    setBusyId("");
    if (res === "success") {
      markDone(id);
      const target = fixTarget;
      (async () => {
        let roster = users;
        if (!roster?.length) {
          roster = (await getAllusers("users").catch(() => [])) || [];
        }
        if (!roster.length) {
          toast.warn(
            "Fix request saved, but no one could be alerted. Let the approver know directly.",
          );
          return;
        }
        await notifyCommissionFlagged(target, roster, currentUser, note);
      })().catch((e) => console.error("Fix notification failed:", e));
      setFixTarget(null);
    } else {
      toast.error("Couldn't send the fix request. Try again.");
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mb-6">
      <div className="px-6 py-4 border-b border-black/[0.06] flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="w-7 h-7 bg-slate-100 text-linkColor rounded-md inline-flex items-center justify-center">
            <Calculator size={14} strokeWidth={2.2} />
          </span>
          <div className="text-[15px] font-semibold text-slate-900 flex items-center gap-2">
            Deals to approve
            {queue.length > 0 && (
              <span className="bg-slate-100 text-linkColor text-[11px] font-bold rounded-full px-2 py-0.5">
                {queue.length}
              </span>
            )}
          </div>
        </div>
        <Link
          to="/dashboard/commission-approvals"
          className="text-[13px] font-medium text-linkColor hover:text-themeColor inline-flex items-center gap-1"
        >
          Open Commission Approvals <ChevronRight size={14} />
        </Link>
      </div>

      {!loaded ? (
        <div className="px-6 py-8 text-center text-gray-400 text-[13px]">
          Loading deals…
        </div>
      ) : queue.length === 0 ? (
        <div className="px-6 py-8 text-center text-gray-400 text-[13px]">
          All caught up. New deals appear here as soon as they're approved.
        </div>
      ) : (
        <div>
          {queue.slice(0, MAX_ROWS).map((deal) => {
            const id = String(deal.createdat);
            return (
              <div
                key={id}
                onClick={() => {
                  // Don't navigate away when she's selecting a figure to copy.
                  if (window.getSelection?.()?.toString()) return;
                  navigate(`/dashboard/deals-detail/${id}`);
                }}
                className="px-6 py-3 border-b border-black/[0.04] last:border-b-0 flex flex-wrap items-center gap-x-4 gap-y-2 cursor-pointer hover:bg-gray-50 transition-colors"
              >
                <div className="min-w-[160px]">
                  <div className="text-[14px] font-semibold text-slate-900">
                    {deal.propertyaddress || "No address"}
                  </div>
                  <div className="text-[12px] text-gray-500">
                    {[deal.agentName, splitLabel(deal)]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </div>
                <span className="flex-1" />
                <div className="text-[14px] font-semibold text-slate-900">
                  {formatCurrency(deal.totalCommissionamount)}
                </div>
                {canAct && (
                  <div
                    className="flex gap-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={() => setFixTarget(deal)}
                      disabled={!!busyId}
                      className="inline-flex items-center gap-1.5 text-[12px] rounded-full px-3 py-1.5 border border-gray-200 text-gray-600 hover:border-amber-400 hover:text-amber-700 disabled:opacity-50"
                    >
                      <Flag size={13} /> Fix
                    </button>
                    <button
                      onClick={() => approveDeal(deal)}
                      disabled={!!busyId}
                      className="inline-flex items-center gap-1.5 text-[12px] rounded-full px-3 py-1.5 bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-50"
                    >
                      <CheckCircle2 size={13} />
                      {busyId === id ? "Saving…" : "Approve"}
                    </button>
                  </div>
                )}
                <ChevronRight size={16} className="text-gray-300" />
              </div>
            );
          })}
          {queue.length > MAX_ROWS && (
            <Link
              to="/dashboard/commission-approvals"
              className="block px-6 py-3 text-center text-[13px] font-medium text-linkColor hover:text-themeColor"
            >
              View all {queue.length} deals
            </Link>
          )}
        </div>
      )}

      {fixTarget && (
        <div onClick={(e) => e.stopPropagation()}>
          <FixDealDialog
            deal={fixTarget}
            busy={!!busyId}
            onCancel={() => setFixTarget(null)}
            onSubmit={submitFix}
          />
        </div>
      )}
    </div>
  );
}
